import { Loading } from 'components/core-v2/pages'
import { useWatch } from 'react-hook-form'

import { ApiAssetWithConstituents } from '../../../api/rtk-api'
import { Card, SelectField, ToggleField, Typography } from '../../../components/core-v2'
import AssetLookthroughTable, {
  ConstituentAsset
} from '../../../components/core-v2/organisms/asset-lookthrough-table'
import { CURRENCY } from '../../../const'
import { ForExRateFn } from '../../../utilities/fx'

export type AssetLookThroughStatelessProps = {
  assetConstituents: ApiAssetWithConstituents[]
  fxRateConversionFn: ForExRateFn
  isLoading: boolean
  onAssetClick: (assetId: string) => void
}

const ALL_REGIONS = {
  label: 'All regions',
  value: 'all'
}
const ALL_TRADES = {
  label: 'All trades',
  value: 'all'
}

const AssetLookThroughStateless = ({
  assetConstituents,
  fxRateConversionFn,
  isLoading,
  onAssetClick
}: AssetLookThroughStatelessProps) => {
  const [regionValue, tradesValue, displayInZARValue] = useWatch({
    name: ['region', 'trades', 'displayInZAR']
  })
  const regionOptions = [ALL_REGIONS]
  const tradeOptions = [ALL_TRADES]

  const constituents = assetConstituents.filter(item => item.constituentAssets?.length > 0)
  const typesAndSectorsToggle = false

  const tryGetFxRateResult = (baseCurrency: string, counterCurrency: string) => {
    if (displayInZARValue) {
      const result = fxRateConversionFn(baseCurrency, counterCurrency)
      if (result.rateFound) {
        return {
          multiplier: fxRateConversionFn(baseCurrency, counterCurrency).multiplier,
          currency: baseCurrency,
          rateFound: true
        }
      }
    }
    return { multiplier: 1, currency: counterCurrency, rateFound: false }
  }

  const map = constituents
    ?.map(asset => asset?.constituentAssets)
    ?.flat()
    ?.reduce((acc, apiConstituent) => {
      const type = apiConstituent?.type
      const sector = apiConstituent?.sector
      const isin = apiConstituent?.isin
      const key = typesAndSectorsToggle ? `${type}-${sector}` : isin

      const canApplyFx = tryGetFxRateResult(CURRENCY, apiConstituent.currency)
      const amountInvestedWithGapRisk = canApplyFx.rateFound
        ? canApplyFx.multiplier * apiConstituent.amountInvestedWithGapRisk
        : apiConstituent.amountInvestedWithGapRisk
      const amountInvestedWithoutGapRisk = canApplyFx.rateFound
        ? canApplyFx.multiplier * apiConstituent.amountInvestedWithoutGapRisk
        : apiConstituent.amountInvestedWithoutGapRisk
      const aum = canApplyFx.rateFound
        ? canApplyFx.multiplier * apiConstituent.aum
        : apiConstituent.aum

      if (acc.has(key)) {
        const existingConstituent = acc.get(key)
        existingConstituent.amountInvestedWithGapRisk += amountInvestedWithGapRisk
        existingConstituent.amountInvestedWithoutGapRisk += amountInvestedWithoutGapRisk
      } else {
        acc.set(key, {
          amountInvestedWithGapRisk,
          amountInvestedWithoutGapRisk,
          aum,
          region: apiConstituent.region,
          currency: canApplyFx.currency,
          originalCurrency: apiConstituent.currency,
          code: apiConstituent.isin,
          name: apiConstituent.instrumentLongName
        })
        if (!regionOptions.some(option => option.value === apiConstituent.region)) {
          regionOptions.push({ value: apiConstituent.region, label: apiConstituent.region })
        }
        if (!tradeOptions.some(option => option.value === apiConstituent.currency)) {
          tradeOptions.push({ value: apiConstituent.currency, label: apiConstituent.currency })
        }
      }
      return acc
    }, new Map<string, ConstituentAsset>())
  let constituentMap: ConstituentAsset[] = map ? Array.from(map.values()) : []

  // Filtering
  if (regionValue !== ALL_REGIONS.value) {
    constituentMap = constituentMap.filter(item => item.region === regionValue)
  }
  if (tradesValue !== ALL_TRADES.value) {
    constituentMap = constituentMap.filter(item => item.originalCurrency === tradesValue)
  }

  if (constituentMap && constituentMap.length > 0) {
    constituentMap.sort((lhs, rhs) => lhs.name.localeCompare(rhs.name))
  }

  const headerMessage = `${constituentMap.length} Constituents`

  return (
    <Card.Wrapper spacing="sm">
      {isLoading ? (
        <Loading className="mt-12" />
      ) : (
        <>
          <div className="d-flex">
            <SelectField id="region" label="Asset look through" options={regionOptions} />
            <SelectField id="trades" label="" options={tradeOptions} />
            <ToggleField className="ml-4" id="displayInZAR" label="Display in SA Rand" />
          </div>

          <Typography className="my-1" variant="sh3">
            {headerMessage}
          </Typography>
          <AssetLookthroughTable
            data={constituentMap}
            onSelectedRow={row => onAssetClick(row.code)}
          />
        </>
      )}
    </Card.Wrapper>
  )
}

export default AssetLookThroughStateless
